export interface Subscription {
  customerName: string
  certFinished: boolean
  hasTenants: boolean
  id: string
  isDeleted: boolean
  creationTime: string
  createdBy: string
  modifiedTime: string
  modifiedBy: string
}

export interface PostSubscription {
  customerName: string
}

export interface Tenant {
  tenantName: string
  tenantDisplayname: string
  environment: string
  subscriptionId: string
  vaultUri: string
  azureDevopsBuildDefinitionId: number
  azureDevopsLastRunUri: string
  activeReleaseLock: boolean
  deployStatus: DeployStatus
  // Null when no successful deploy has been recorded. Gates deletion: a destroy runs
  // current module versions against state written by an older apply.
  lastSuccessfulDeployUtc: string | null
  // UTC instant ("…Z") of the local day the Entra ID client secret expires; null when not
  // recorded. Shown and compared as a local date (helpers.clientSecretExpiryStatus).
  clientSecretExpiresOnUtc: string | null
  id: string
  isDeleted: boolean
  creationTime: string
  createdBy: string
  modifiedTime: string
  modifiedBy: string
}

export type DeployStatus =
  | 'notCreated'
  | 'initializing'
  | 'initializingFailed'
  | 'initialized'
  | 'updating'
  | 'deleting'
  | 'updatingFailed'
  | 'pendingDelete'
  | 'finished'
  | 'unknown'
  | 'feWaiting'
  | 'supportLoading'

export interface PostTenant {
  tenantName: string
  tenantDisplayname: string
  environment: string
}

export interface SubscriptionWithTenants {
  subscription: Subscription
  tenants: Tenant[]
}

export interface TenantConfig {
  feRunMode: 'Full' | 'NoCustomers' | 'CustomersOnly'
  feHostname: string
  feLanguage: string
  feLanguageCode: string
  feAvailableLanguages: string
  fePortalUrl: string
  suiteVersion: string
  supportPassword: string
  gateway1CustomerAplicationId: string
  gateway1CustomerHostname: string
  gateway1Whitelisting: string
  customersUIHostname: string
  customersBackendPortForward: string
  customersMailEnabled: boolean
  customersChatEnabled: boolean
  customersTelephonyEnabled: boolean
  customersTasksEnabled: boolean
  customersCaseflowEnabled: boolean
  customersDossiersEnabled: boolean
  customersPhoneHealthCheckEnabled: boolean
  customersDisableChatNotifications: boolean
  queryConfigFromGraph: boolean
  enableCMSPackages: boolean
  enablePortalUIPackages: boolean
  translationProviderWidgets: boolean
  translationProviderSuite: boolean
  socialDashboardId: string
  socialTeamId: string
  socialThemeId: string
  matomoInteralHostname: string
  matomoInteralSiteId: number
  matomoInternalContainerId: string
  matomoExternalHostname: string
  matomoExternalSiteId: number
  logRocketApiKey: string
  googleAnalyticsId: string
  socialContentMigrationEnabled: boolean
  authenticationType: string
  clientId: string
  clientSecret: string
  idpAzureAdTenantId: string
  idpAdditionalOidcProviders: string
  idpAzureHostname: string
  idpAzureDisplayName: string
  idpAzureDisplayOrder: string
  idpSkipKeycloakLoginPage: boolean
  idpEmployeesAlias: string
  idpDomainHint: string
  idpLoginPrompt: string
  idpOverrideBrowserFlow: boolean
  idpForceOTPBrowserFlow: boolean
  idpWebcenterEnabled: boolean
  idpWebcenterHostname: string
  idpWebcenterClientId: string
  idpWebcenterClientSecret: string
  idpCustomersPortalsEnabled: boolean
  idpCustomersPortalsHostname: string
  idpCustomersPortalsClientId: string
  idpCustomersPortalsClientSecret: string
  idpFormsPasswordPolicy: string
  idpLoginTheme: string
  idpHousingKeycloakEnabled: boolean
  idpHousingKeycloakHostname: string
  idpHousingKeycloakOidcEnabled: boolean
  idpHousingKeycloakOidcBaseUrl: string
  idpHousingKeycloakOidcClientId: string
  idpHousingKeycloakOidcClientSecret: string
  idpHousingKeycloakCiTestClientEnabled: boolean
  idpHousingKeycloakImportEnabled: boolean
  idpHousingKeycloakImportRealm: string
  idpHousingKeycloakImportIdentityClientId: string
  idpHousingKeycloakImportHousingClientId: string
  idpHousingKeycloakImportPortalClient: boolean
  idpUmbracoClientEnabled: boolean
  idpPublicPortalThemeId: string
  portalWidgetPacks: string
  helloIdEnabled: boolean
  helloIdClientId: string
  helloIdClientSecret: string
  helloIdTenantId: string
  helloIdAzureAdClientSecret: string
  publicPortalThemingEnabled: boolean
  useHiveSubscriptions: boolean
  useHiveGateway: boolean
  enableGatewayApi: boolean
  enableGatewayApiWs: boolean
  // JSON object: ingress hostname -> ClusterIssuer name. Runtime-managed: edited on the
  // Runtime tab (TenantCertificates), and ignored by the Deploytime save.
  certificateIssuers: string
  sharedPaymentsProvider: string
  sharedPaymentsProviderConfiguration: string
  liquitUrl: string
  customScopes: string
  clientwebIpRanges: string
  // Portal UI Module fields
  portalUIHostname: string
  portalUIPortalId: string
  portalUIPortalName: string
  portalUIPortalIdpHint: string
  portalUIPortalReleaseVersion: string
  portalUIPortalOsmUrl: string
  portalUIPortalStreetviewUrl: string
  portalUIMediaHandlerUrl: string
  portalUIMediaHandlerMaxFileSize: number
  portalUIMediaHandlerAcceptedFileTypes: string
  portalUIMediaHandlerAttachmentTypeId: string
  portalUICookieConsentManifestUrl: string
  portalUICustomGateway: string
  portalUIPortalClient: string
  portalUIEnableWww: boolean
  portalUIExtraHostnames: string
  portalUICustomAuthority: string
  portalUIUmamiWebsiteId: string
  portalUIUmamiScriptUrl: string
  portalUITolkieToken: string
  portalUITolkieState: string
}

export interface FeatureFlags {
  name: string
  enabled: boolean
  label: string
  description: string
  hasTenantFilter: boolean
}
export interface Audit {
  action: {
    traceId: string
    httpMethod: 'GET' | 'POST' | 'PUT' | 'DELETE'
    userName: string
    actionParameters: {
      tenantid: string
    }
  }
  startDate: string
  eventType: string
  id: string
  isDeleted: boolean
  creationTime: string
  createdBy: string
  modifiedTime: string
  modifiedBy: string
}

// Cross-tenant Terraform-module overview (GET /api/modulecollection/overview)
export interface ModuleOverviewPackage {
  name: string
  latestVersion: string | null
  latestVersionSource: 'feed' | 'observed' | null
  // What devops/pipelines/tf-build.yml pins (e.g. "0.28.*") and what that resolves to on the
  // feed: the version a deploy installs, which outdatedCount and isOutdated compare against.
  // Null when the package isn't pinned or the feed can't be read; latestVersion is used then.
  pinnedVersion: string | null
  expectedVersion: string | null
  tenantCount: number
  outdatedCount: number
}

export interface ModuleOverviewCell {
  version: string
  buildId: number | null
  isOutdated: boolean
}

export interface ModuleOverviewRow {
  tenantId: string
  tenantName: string
  tenantDisplayName: string | null
  environment: string
  subscriptionId: string
  // Null when the tenant has no module information recorded (modules is then empty).
  timestamp: string | null
  modules: Record<string, ModuleOverviewCell>
}

export interface ModuleOverviewResponse {
  totalCount: number
  skip: number
  take: number
  packages: ModuleOverviewPackage[]
  rows: ModuleOverviewRow[]
}

export interface BaseConfig {
  host: string
}

export interface EmbraceTenantsConfiguration {
  host: string
  authClientId: string
  authAuthority: string
  authScope: string
  environments: string[]
  // Per-tenant monitoring (docs/grafana-tenant-monitoring.md): the Grafana of this
  // segment and the dashboard that takes the tenant name in its `tenant` variable.
  // Both come from the TMA-labelled ClientApp:Grafana* App Configuration keys and
  // are absent until configured, which hides the monitoring buttons.
  grafanaUrl?: string
  grafanaTenantDashboardPath?: string
}

export interface Notification {
  severity: number
  starttime: string
  endtime: string
  content: NotificationContent
}

export interface NotificationContent {
  en: {
    main: string
    title: string
  }
  nl: {
    main: string
    title: string
  }
}

export interface NotificationsState {
  featureFlags: Audit[]
  featureFlagsLoaded: boolean
  notifications: Notification[]
  value?: {
    from: Date
    to: Date
  }
  newNotification: {
    severity: number
    starttime: string
    endtime: string
    content: string
  }
}

export interface MaintenanceType {
  hasMaintenance: boolean
  startTime?: string
  endTime?: string
  url?: string
  key?: string
  title?: {
    en?: string
    nl?: string
  }
  description?: {
    en?: string
    nl?: string
  }
}

export interface NewMaintenance {
  startTime: string
  endTime: string
  title: string
  description: string
}

export interface DateRange {
  from: Date
  to: Date
}

export interface BreadcrumbsData {
  trigger: boolean
  data: {
    subscriptionId?: string
  }
}

export interface FeatureFlag {
  label: string
  name: string
  description: string
  enabled: boolean
  enabledFor?: string[]
  hasTenantFilter: boolean
  tags: Record<string, string>
}

export const PRIORITY_INFO = 1
export const PRIORITY_NORMAL = 3
export const PRIORITY_HIGH = 5

export interface Filters {
  environment: { options: string[]; selected: string[] }
  deployStatus: { options: string[]; selected: string[] }
  showSubscriptionsWithTenant: boolean
  showSubscriptionsWithoutTenant: boolean
}

export type SetFilters = React.Dispatch<React.SetStateAction<Filters>>

// Tenant-configuration search filters (GET /api/search/availableFilters,
// /api/search/configValues, /api/search/search). Hand-mirrored from the API DTOs —
// there is no code generation, so these names must stay byte-identical (WI 159594).
export type TenantConfigFilterValueType = 'string' | 'boolean' | 'number'

export interface TenantConfigFilterKey {
  key: string
  label: string
  valueType: TenantConfigFilterValueType
  // The closed value list the model declares for the key ([Enum]), null for free text.
  allowedValues: string[] | null
  // Sample values ([Examples]) for free-text keys whose stored form is not obvious.
  examples: string[] | null
}

// One configuration key being filtered on, with the selected values. `values` empty
// means the key is picked but nothing is selected yet, which is not a filter. Also the
// shape of a filter in the search assistant's answer (ConfigFilterSelection in the API).
export interface ConfigFilter {
  key: string
  values: string[]
}

export interface TenantConfigValuesResponse {
  key: string
  values: string[]
}

// Keyed by tenant id, then by configuration key, carrying only the keys the search
// filtered on. A null value means the tenant has no value for that key.
export type TenantConfigValues = Record<string, Record<string, string | null>>

// One configuration line on a search result card: the key's operator-facing label
// paired with this tenant's value.
export interface TenantConfigDisplayValue {
  label: string
  value: string | null
}

// Terraform-module version search filters (GET /api/search/availableFilters,
// /api/search/moduleVersions, /api/search/search). Hand-mirrored from the API DTOs,
// the same way the configuration filters above are.

// One module package being filtered on, with the selected versions. `versions` empty
// means the package is picked but nothing is selected yet, which is not a filter.
// `negate` flips the selection from "is one of these" to "is none of these" — the
// "which tenants still need updating" question.
export interface ModuleFilter {
  package: string
  versions: string[]
  negate: boolean
}

export interface ModuleVersionsResponse {
  package: string
  versions: string[]
}

// Keyed by tenant id, then by package name, carrying only the packages the search
// filtered on. A null value means the tenant records no version for that package.
export type TenantModuleVersions = Record<string, Record<string, string | null>>

// POST /api/search/assistant/interpret: the filters the operator's sentence resolved
// to, already validated against the catalog. Mirrors SearchInterpretationResponse in
// the API; property names must stay byte-identical. Whether the assistant is available
// at all comes with GET /api/search/availableFilters (searchAssistantEnabled).
export interface SearchInterpretation {
  query: string | null
  environments: string[]
  deployStatuses: string[]
  includeDeleted: boolean
  configFilters: ConfigFilter[]
  // Same shape as the page's own module filters; every package and version passed the
  // API's check against what tenants record.
  moduleFilters: ModuleFilter[]
  explanation: string
  unresolved: string[]
}

// A tenant that points at least one hostname at a certificate issuer.
export interface CertificateIssuerUsage {
  tenantId: string
  tenantName: string
  environment: string
  subscriptionId: string
  hostnames: string[]
}

// Mirrors ClusterIssuerState in tenants.api (Services/Certificates/IClusterIssuerSync.cs).
export type ClusterIssuerState = 'Disabled' | 'Unknown' | 'Missing' | 'NotManaged' | 'NotReady' | 'Ready'

// Mirrors CertificateIssuerClusterInfo: which cluster this TMA instance writes issuers to.
export interface CertificateIssuerClusterInfo {
  enabled: boolean
  clusterName?: string | null
}

// Mirrors CertificateIssuerDto: an ACME ClusterIssuer TMA manages. The EAB HMAC key is
// write-only; only whether one is stored comes back.
export interface CertificateIssuer {
  id: string
  name: string
  displayName: string
  description?: string | null
  acmeServerUrl: string
  eabKeyId?: string | null
  hasEabHmacKey: boolean
  clusterState: ClusterIssuerState
  clusterMessage?: string | null
  createdBy?: string | null
  createdAtUtc: string
  updatedBy?: string | null
  updatedAtUtc?: string | null
  usedBy: CertificateIssuerUsage[]
}

// Mirrors TenantCertificatesDto (GET /api/tenant/{id}/certificates).
export interface TenantIngressCertificate {
  key: 'suite' | 'portalUi'
  label: string
  ingressName: string
  hostnames: string[]
  selectedIssuer?: string | null
  liveState: ClusterIssuerState
  liveIssuer?: string | null
  certificateIssuer?: string | null
  certificateReady?: boolean | null
  message?: string | null
}

export interface TenantCertificates {
  clusterEnabled: boolean
  clusterName?: string | null
  servesEnvironment: boolean
  deployRunning: boolean
  defaultIssuer: string
  ingresses: TenantIngressCertificate[]
}

export interface TenantIngressApplyResult {
  key: string
  outcome: 'Applied' | 'Unchanged' | 'NotDeployed' | 'Disabled' | 'Failed'
  message?: string | null
}

// Mirrors CertificateTenantRef: the tenant whose ingress a certificate belongs to.
export interface CertificateTenantRef {
  tenantId: string
  tenantName: string
  subscriptionId: string
}

// Mirrors CertificateDto: a cert-manager Certificate, read live from the cluster.
export interface ClusterCertificate {
  environment: string
  name: string
  ingressName?: string | null
  issuer?: string | null
  dnsNames: string[]
  ready: boolean
  message?: string | null
  notAfter?: string | null
  renewalTime?: string | null
  lastFailureTime?: string | null
  issuing: boolean
  revision?: number | null
  tenant?: CertificateTenantRef | null
}

export type CertificateRequestState = 'Pending' | 'Issued' | 'Failed' | 'Denied'

// Mirrors CertificateRequestDto: one issuance attempt of a Certificate.
export interface ClusterCertificateRequest {
  environment: string
  name: string
  certificateName?: string | null
  issuer?: string | null
  revision?: number | null
  createdAt?: string | null
  state: CertificateRequestState
  message?: string | null
  failureTime?: string | null
  challengeMessage?: string | null
  dnsNames: string[]
  tenant?: CertificateTenantRef | null
}

// Mirrors CertificateOverviewDto.
export interface CertificateOverview {
  clusterEnabled: boolean
  clusterName?: string | null
  certificates: ClusterCertificate[]
  requests: ClusterCertificateRequest[]
  errors: string[]
}
